import {
	ConfirmProductImageUploadMutationFn,
	GenerateProductImageUploadUrlMutationFn,
	ProductImageCropUnit,
} from '@codezo/graphql';
import type { ImageCropCoords } from '../components/ui/image-cropper';
import { MAX_UPLOAD_SIZE, MAX_UPLOAD_SIZE_LABEL } from './file-size';
import { DESIGN_ASSET_ACCEPT, DESIGN_ASSET_LABEL, matchesAccept } from './file-types';

/* ── Types ── */

/** An image staged in the UI before it has been uploaded to a product. */
export interface StagedImage {
	file: File;
	/** Object URL for image previews; empty string for non-image files (PDF/EPS/AI). */
	preview: string;
	/** Optional display crop chosen before upload; applied on confirm. */
	crop?: ImageCropCoords | null;
}

/* ── Constants ── */

/** `accept` attribute string for file inputs. */
export const IMAGE_ACCEPT_ATTR = DESIGN_ASSET_ACCEPT;

export const MAX_IMAGE_SIZE = MAX_UPLOAD_SIZE;

/** Human-readable summary of accepted formats / size, for dropzone hints. */
export const IMAGE_ACCEPT_HINT = `${DESIGN_ASSET_LABEL} — max ${MAX_UPLOAD_SIZE_LABEL}`;

/* ── Helpers ── */

function isAcceptedFile(file: File): boolean {
	return file.size <= MAX_IMAGE_SIZE && matchesAccept(file, IMAGE_ACCEPT_ATTR);
}

/**
 * Filter a FileList/array down to acceptable files and wrap each as a
 * {@link StagedImage}. Image files get an object-URL preview; others get none.
 */
export function stageFiles(files: FileList | File[] | null): StagedImage[] {
	if (!files) return [];
	const staged: StagedImage[] = [];
	for (const file of Array.from(files)) {
		if (!isAcceptedFile(file)) continue;
		const isImage = file.type.startsWith('image/');
		staged.push({
			file,
			preview: isImage ? URL.createObjectURL(file) : '',
		});
	}
	return staged;
}

/** Best-effort natural dimension extraction for image files. */
async function extractDimensions(
	image: StagedImage
): Promise<{ width: number; height: number } | undefined> {
	if (!image.file.type.startsWith('image/') || !image.preview) return undefined;
	try {
		const img = new window.Image();
		img.src = image.preview;
		await new Promise((resolve, reject) => {
			img.onload = resolve;
			img.onerror = reject;
		});
		return { width: img.naturalWidth, height: img.naturalHeight };
	} catch {
		return undefined;
	}
}

/**
 * Upload a single staged image to an existing product:
 * generate signed URL → PUT to blob storage → confirm.
 *
 * `onProgress` reports 0→1 for this single file across the three phases so
 * callers can drive an aggregate progress bar.
 */
export async function uploadProductImage(args: {
	productId: string;
	image: StagedImage;
	isPrimary: boolean;
	generateUploadUrl: GenerateProductImageUploadUrlMutationFn;
	confirmUpload: ConfirmProductImageUploadMutationFn;
	onProgress?: (fraction: number) => void;
}): Promise<void> {
	const { productId, image, isPrimary, generateUploadUrl, confirmUpload, onProgress } = args;

	onProgress?.(0.1);

	const { data: urlData } = await generateUploadUrl({
		variables: {
			input: {
				productId,
				fileName: image.file.name,
				fileType: image.file.type,
				fileSize: image.file.size,
			},
		},
	});

	const upload = urlData?.generateProductImageUploadUrl;
	if (!upload?.uploadUrl || !upload.imageId || !upload.blobUrl) {
		throw new Error(`Failed to generate upload URL for ${image.file.name}`);
	}

	onProgress?.(0.4);

	const res = await fetch(upload.uploadUrl, {
		method: 'PUT',
		body: image.file,
		headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': image.file.type },
	});
	if (!res.ok) throw new Error(`Failed to upload ${image.file.name}`);

	onProgress?.(0.7);

	const dimensions = await extractDimensions(image);

	await confirmUpload({
		variables: {
			input: {
				productId,
				imageId: upload.imageId,
				fileName: image.file.name,
				fileType: image.file.type,
				fileSize: image.file.size,
				blobUrl: upload.blobUrl,
				isPrimary,
				dimensions,
				crop: image.crop
					? {
							x: image.crop.x,
							y: image.crop.y,
							width: image.crop.width,
							height: image.crop.height,
							zoom: image.crop.zoom,
							rotation: image.crop.rotation,
							aspectRatio: image.crop.aspectRatio,
							unit: ProductImageCropUnit.Percent,
						}
					: undefined,
			},
		},
	});

	onProgress?.(1);
}
